import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Minimize2, ExternalLink } from 'lucide-react';
import { DriveFile } from '../../lib/services/googleDriveService';
import { useMinimizedFiles } from '../../hooks/useMinimizedFiles';

interface FileViewerModalProps {
  file: DriveFile | null;
  isOpen: boolean;
  onClose: () => void;
  onMinimize?: () => void;  // Callback when file is minimized (e.g., to refresh file list)
  originRect?: DOMRect | null;  // Position of the clicked file element
}

type AnimationState = 'entering' | 'visible' | 'minimizing' | null;

// Helper to get the minimized files icon position
const getMinimizedIconRect = (): DOMRect | null => {
  const icon = document.querySelector('[data-minimized-files-icon="true"]');
  return icon?.getBoundingClientRect() || null;
};

export function FileViewerModal({ file, isOpen, onClose, onMinimize, originRect }: FileViewerModalProps) {
  const [animationState, setAnimationState] = useState<AnimationState>(null);
  const [iframeError, setIframeError] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const { addMinimizedFile } = useMinimizedFiles();

  // Handle opening animation
  useEffect(() => {
    if (isOpen && file) {
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 250);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setAnimationState(null);
    }
  }, [isOpen, file]);

  // Reset iframe error when file changes
  useEffect(() => {
    if (file) {
      setIframeError(false);
    }
  }, [file?.id]);

  // Calculate CSS variables for opening animation (expand from clicked file)
  const getOriginStyle = (): React.CSSProperties => {
    if (!originRect) {
      // Fallback: expand from center
      return {
        '--origin-x': '50%',
        '--origin-y': '50%',
      } as React.CSSProperties;
    }

    const centerX = originRect.left + originRect.width / 2;
    const centerY = originRect.top + originRect.height / 2;

    return {
      '--origin-x': `${centerX}px`,
      '--origin-y': `${centerY}px`,
    } as React.CSSProperties;
  };

  // Calculate CSS variables for minimize animation (shrink toward icon)
  const getMinimizeStyle = (): React.CSSProperties => {
    const iconRect = getMinimizedIconRect();
    const isMobile = window.innerWidth < 768;

    // Fallback for mobile (icon is hidden) or if icon not found
    if (isMobile || !iconRect) {
      return {
        '--minimize-x': '0px',
        '--minimize-y': '-50vh',
      } as React.CSSProperties;
    }

    // Calculate the offset from modal center to icon center
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;
    const iconCenterX = iconRect.left + iconRect.width / 2;
    const iconCenterY = iconRect.top + iconRect.height / 2;

    return {
      '--minimize-x': `${iconCenterX - viewportCenterX}px`,
      '--minimize-y': `${iconCenterY - viewportCenterY}px`,
    } as React.CSSProperties;
  };

  // Handle minimize with animation
  const handleMinimize = () => {
    if (!file) return;

    console.log('[FileViewerModal] Minimizing file:', file.name);
    setAnimationState('minimizing');

    // Wait for animation to complete before actually closing
    setTimeout(() => {
      addMinimizedFile(file);
      onClose();
      onMinimize?.(); // Notify parent (e.g., to refresh file list)
    }, 300);
  };

  // Handle close with confirmation
  const handleClose = () => {
    const confirmed = window.confirm(`Sei sicuro di voler chiudere "${getFileName()}"?`);
    if (confirmed) {
      onClose();
    }
  };

  if (!isOpen || !file) return null;

  const getFileUrl = () => {
    // Try different URL options for embedding
    if (file.preview_url) return file.preview_url;
    if (file.url) return file.url;
    // Google Drive embed URL
    return `https://drive.google.com/file/d/${file.id}/preview`;
  };

  const getFileName = () => {
    return file.name.length > 50 ? `${file.name.substring(0, 47)}...` : file.name;
  };

  const getFileTypeName = (mimeType: string | undefined) => {
    if (!mimeType) return 'FILE';

    const mimeTypeLower = mimeType.toLowerCase();

    // Google Workspace types
    if (mimeTypeLower.includes('google-apps.document')) return 'DOCUMENTO';
    if (mimeTypeLower.includes('google-apps.spreadsheet')) return 'FOGLIO';
    if (mimeTypeLower.includes('google-apps.presentation')) return 'SLIDE';
    if (mimeTypeLower.includes('google-apps.drawing')) return 'DISEGNO';
    if (mimeTypeLower.includes('google-apps.form')) return 'MODULO';
    if (mimeTypeLower.includes('google-apps.folder')) return 'CARTELLA';

    // Common file types
    if (mimeTypeLower.includes('pdf')) return 'PDF';
    if (mimeTypeLower.includes('image')) return 'IMMAGINE';
    if (mimeTypeLower.includes('video')) return 'VIDEO';
    if (mimeTypeLower.includes('audio')) return 'AUDIO';
    if (mimeTypeLower.includes('text')) return 'TESTO';
    if (mimeTypeLower.includes('word') || mimeTypeLower.includes('docx')) return 'WORD';
    if (mimeTypeLower.includes('excel') || mimeTypeLower.includes('xlsx')) return 'EXCEL';
    if (mimeTypeLower.includes('powerpoint') || mimeTypeLower.includes('pptx')) return 'POWERPOINT';

    // Fallback
    return mimeType.split('/')[1]?.toUpperCase() || 'FILE';
  };

  // Determine animation classes
  const modalAnimationClass =
    animationState === 'entering' ? 'file-viewer-modal-enter' :
    animationState === 'minimizing' ? 'file-viewer-modal-minimize' : '';

  const backdropAnimationClass =
    animationState === 'minimizing' ? 'file-viewer-backdrop-minimize' : '';

  // Determine animation styles
  const animationStyle =
    animationState === 'entering' ? getOriginStyle() :
    animationState === 'minimizing' ? getMinimizeStyle() : {};

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity ${backdropAnimationClass}`}
        onClick={handleClose}
      />

      {/* Modal Content with animation */}
      <div
        ref={modalRef}
        className={`relative w-full h-full bg-white shadow-xl overflow-hidden ${modalAnimationClass}`}
        style={animationStyle}
      >
        {/* Header - 6mm height */}
        <div className="flex items-center justify-between px-4 py-1 border-b border-gray-200 bg-gray-50" style={{ height: '6mm' }}>
          <div className="flex items-center space-x-2 min-w-0 flex-1">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {getFileName()}
            </h3>
            <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded flex-shrink-0">
              {getFileTypeName(file.mime_type)}
            </span>
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.open(getFileUrl(), '_blank');
              }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Apri in nuova scheda"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMinimize();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Minimizza"
            >
              <Minimize2 className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Chiudi"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* File Content */}
        <div className="overflow-hidden" style={{ height: 'calc(100vh - 6mm)' }}>
          {iframeError ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 bg-gray-50">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Impossibile visualizzare l'anteprima del file
                </p>
                <p className="text-xs text-gray-400">
                  Il file potrebbe richiedere permessi aggiuntivi
                </p>
              </div>
              <button
                onClick={() => window.open(`https://drive.google.com/file/d/${file.id}/view`, '_blank')}
                className="flex items-center gap-2 px-4 py-2 bg-[#2383E2] text-white text-sm font-medium rounded-md hover:bg-[#1a6bc2] transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Apri in Google Drive
              </button>
            </div>
          ) : (
            <iframe
              src={getFileUrl()}
              className="w-full h-full border-0"
              title={file.name}
              allow="fullscreen"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              onError={() => setIframeError(true)}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at the root level
  return createPortal(modalContent, document.body);
}
